import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { brand } from '@/theme/theme';
import {
  TERMS_INTRO,
  TERMS_SECTIONS,
  TERMS_EFFECTIVE_DATE,
  PRIVACY_INTRO,
  PRIVACY_SECTIONS,
  PRIVACY_EFFECTIVE_DATE,
} from '@/content/legalContent';

export type LegalDocType = 'terms' | 'privacy';

interface LegalDialogProps {
  open: boolean;
  doc: LegalDocType | null;
  onClose: () => void;
}

const DOC_CONFIG = {
  terms: { title: 'Terms & Conditions', intro: TERMS_INTRO, sections: TERMS_SECTIONS, effectiveDate: TERMS_EFFECTIVE_DATE },
  privacy: { title: 'Privacy Policy', intro: PRIVACY_INTRO, sections: PRIVACY_SECTIONS, effectiveDate: PRIVACY_EFFECTIVE_DATE },
} as const;

/**
 * Shows the Terms & Conditions or Privacy Policy in a modal dialog rather
 * than navigating away — used on the registration form so reading either
 * document never loses in-progress form input and never disturbs browser
 * history (no Back-button surprises).
 */
export function LegalDialog({ open, doc, onClose }: LegalDialogProps) {
  if (!doc) return null;
  const config = DOC_CONFIG[doc];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontWeight: 800, color: brand.navy, fontSize: 18 }}>
          {config.title}
        </Typography>
        <Typography sx={{ color: brand.inkSoft, fontSize: 12, mt: 0.25 }}>
          Effective date: {config.effectiveDate} &nbsp;·&nbsp; Ardinotech Private Limited
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        {config.intro.map((p, i) => (
          <Typography key={i} sx={{ color: brand.ink, lineHeight: 1.7, mb: 2, fontSize: 14 }}>
            {p}
          </Typography>
        ))}

        {config.sections.map((s, i) => (
          <Box key={i} sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 700, color: brand.navy, fontSize: 15.5, mb: 1 }}>
              {i + 1}. {s.heading}
            </Typography>
            {s.body?.map((p, j) => (
              <Typography key={j} sx={{ color: brand.ink, lineHeight: 1.7, mb: 1, fontSize: 14 }}>
                {p}
              </Typography>
            ))}
            {s.bullets && (
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {s.bullets.map((b, j) => (
                  <Typography key={j} component="li" sx={{ color: brand.ink, lineHeight: 1.7, mb: 0.65, fontSize: 14 }}>
                    {b}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}
